'use client';

import type { FC } from 'react';
import type { CategoryFilteredTicketRankingWidgetData } from '@weberus/shared-types/dist/display/widgets/category-filtered-ticket-ranking';
import type { HomeLogPageNameValue } from '@/app/(pages)/sub-home/[category]/constants';
import type { Swiper as SwiperCore } from 'swiper';
import { useRef, useState, useMemo } from 'react';
import { useAtomValue } from 'jotai';
import { chunk } from 'lodash-es';
import { SubHeader } from '@yanolja-rnd/nol-design-system';
import { clientLogProtobuf } from '@weberus/client-tracker/utils';
import { pcWidthAtom } from '@/app/_shared/store/device';
import { ShowAllButton } from '@/app/_components/common/show-all-button';
import { TicketProductArea } from '@/app/_components/widgets/category-filtered-ticket-ranking/ticket-product-area';
import { TabArea } from '@/app/_components/widgets/category-filtered-ticket-ranking/tab-area';
import { HomePageLog } from '@/app/_shared/protobuf-log/clientlogs';

export interface CategoryFilteredTicketRankingProps {
  data: CategoryFilteredTicketRankingWidgetData;
  pageNameLog: HomeLogPageNameValue;
}

export const CategoryFilteredTicketRanking: FC<CategoryFilteredTicketRankingProps> = ({ data, pageNameLog }) => {
  const { header, items, serverLogMeta } = data;
  const { isPcWidth } = useAtomValue(pcWidthAtom);

  const swiperRef = useRef<SwiperCore | null>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const tabLabels = items.filter((item) => !!item.header?.title).map((i) => i.header?.title) as string[];
  const hasTab = tabLabels.length > 0;

  // 각 탭의 첫 번째 슬라이드 인덱스를 계산하는 함수
  const getTabFirstSlideIndex = useMemo(() => {
    const tabFirstSlideIndexes: number[] = [];
    let slideIndex = 0;

    items.forEach((item, tabIndex) => {
      tabFirstSlideIndexes[tabIndex] = slideIndex;
      const itemsPerSlide = isPcWidth ? 5 : 1;
      const chunks = chunk(item.items, itemsPerSlide);
      slideIndex += chunks.length;
    });

    return tabFirstSlideIndexes;
  }, [items, isPcWidth]);

  const more = hasTab ? items[selectedIndex]?.header?.more : header?.more;
  const moreTitle = more?.title;
  const moreHref = more?.action?.web;

  const isRankItem = !!items?.[0].items?.[0].ranking;

  const handleChangeTab = (index: number) => {
    setSelectedIndex(index);

    // 해당 탭의 첫 번째 슬라이드로 이동
    const targetSlideIndex = getTabFirstSlideIndex[index];
    if (targetSlideIndex !== undefined) {
      if (isPcWidth) {
        swiperRef.current?.slideToLoop(targetSlideIndex);
      } else {
        swiperRef.current?.slideTo(targetSlideIndex);
      }
    }
    clientLogProtobuf(HomePageLog.TabWidgetTitle.click, { logMeta: items[index].serverLogMeta })();
  };

  if (!isRankItem) {
    return null;
  }

  return (
    <div className="my-16 w-full pc:my-40 pc:w-auto pc:max-w-pc-size">
      {!!header?.title && <SubHeader className="px-16" title={header.title} subTitle={header.subTitle} />}
      {!!hasTab && <TabArea tabLabels={tabLabels} selectedIndex={selectedIndex} onChangeTab={handleChangeTab} />}
      <TicketProductArea
        pageNameLog={pageNameLog}
        widgetLogMeta={serverLogMeta || {}}
        tabItems={items.map((item) => item.items)}
        tabLogMeta={items[selectedIndex].serverLogMeta || {}}
        setSelectedIndex={setSelectedIndex}
        swiperRef={swiperRef}
      />
      {!!moreTitle && !!moreHref && (
        <ShowAllButton
          boldTitle={hasTab ? items[selectedIndex].header?.title : undefined}
          title={moreTitle}
          href={moreHref}
          onClick={() => {
            clientLogProtobuf(HomePageLog.WidgetTabTitleButtonSeeAll.click, { logMeta: more?.serverLogMeta })();
          }}
        />
      )}
    </div>
  );
};
